import React, { useState, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router';
import { PageHero } from '../components/PageHero';
import { Section } from '../components/Section';
import { Card } from '../components/Card';
import { Button } from '../components/Button';
import { EmptyState } from '../components/EmptyState';
import useTheme from '../hooks/useTheme';
import { megaGreenData } from '../data/data';
import { CiSearch } from 'react-icons/ci';
import { getPillStyle } from '../utils/styleVariables';

export const Projects: React.FC = () => {
    const { theme, breakpoint } = useTheme();
    const [searchParams, setSearchParams] = useSearchParams();
    const [searchText, setSearchText] = useState(searchParams.get('search') || '');
    const [selectedService, setSelectedService] = useState(searchParams.get('service') || '');
    const [selectedSector, setSelectedSector] = useState(searchParams.get('sector') || '');
    const [selectedLocation, setSelectedLocation] = useState(searchParams.get('location') || '');
    const itemsPerPage = 12;

    // Read page from URL params
    const pageFromUrl = parseInt(searchParams.get('page') || '1', 10);
    const [currentPage, setCurrentPage] = useState(pageFromUrl);

    // Sync with URL params
    React.useEffect(() => {
        const urlPage = parseInt(searchParams.get('page') || '1', 10);
        if (urlPage !== currentPage && urlPage >= 1) {
            setCurrentPage(urlPage);
        }
    }, [searchParams]);

    const allProjects = megaGreenData.projects.projects;

    const filteredProjects = useMemo(() => {
        return allProjects.filter(project => {
            const matchesSearch = !searchText ||
                project.client.toLowerCase().includes(searchText.toLowerCase()) ||
                project.id.toLowerCase().includes(searchText.toLowerCase()) ||
                project.location.toLowerCase().includes(searchText.toLowerCase());

            const matchesService = !selectedService ||
                project.primaryServiceLine.toLowerCase().includes(selectedService.toLowerCase());

            const matchesSector = !selectedSector ||
                project.sector.toLowerCase().includes(selectedSector.toLowerCase());

            const matchesLocation = !selectedLocation ||
                project.location.toLowerCase().includes(selectedLocation.toLowerCase());

            return matchesSearch && matchesService && matchesSector && matchesLocation;
        });
    }, [searchText, selectedService, selectedSector, selectedLocation, allProjects]);

    const uniqueServices = Array.from(new Set(allProjects.map(p => p.primaryServiceLine)));
    const uniqueSectors = Array.from(new Set(allProjects.map(p => p.sector)));
    const uniqueLocations = Array.from(new Set(allProjects.map(p => p.location)));

    // Pagination calculations
    const totalPages = Math.max(1, Math.ceil(filteredProjects.length / itemsPerPage));
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedProjects = filteredProjects.slice(startIndex, endIndex);

    // Ensure currentPage is valid
    React.useEffect(() => {
        if (currentPage > totalPages && totalPages > 0) {
            setCurrentPage(totalPages);
        }
    }, [totalPages]);

    // Reset to page 1 when filters change
    React.useEffect(() => {
        if (currentPage > 1) {
            setCurrentPage(1);
        }
    }, [searchText, selectedService, selectedSector, selectedLocation]);

    // Update URL params
    React.useEffect(() => {
        const params = new URLSearchParams();
        if (searchText) params.set('search', searchText);
        if (selectedService) params.set('service', selectedService);
        if (selectedSector) params.set('sector', selectedSector);
        if (selectedLocation) params.set('location', selectedLocation);
        if (currentPage > 1) params.set('page', currentPage.toString());
        setSearchParams(params);
    }, [searchText, selectedService, selectedSector, selectedLocation, currentPage, setSearchParams]);

    const clearFilters = () => {
        setSearchText('');
        setSelectedService('');
        setSelectedSector('');
        setSelectedLocation('');
        setCurrentPage(1);
        setSearchParams({});
    };

    return (
        <>
            <PageHero
                title="Projects"
                subtitle={megaGreenData.projects.intro}
            />

            <Section>
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '16px',
                        marginBottom: '32px',
                    }}
                >
                    <div
                        style={{
                            display: 'grid',
                            gridTemplateColumns: breakpoint.xs || breakpoint.sm ? '1fr' : '2fr 1fr 1fr 1fr auto',
                            gap: '12px',
                            alignItems: 'end',
                        }}
                    >
                        <div>
                            <label
                                style={{
                                    display: 'block',
                                    fontSize: '14px',
                                    fontWeight: 500,
                                    color: theme.text.primary,
                                    marginBottom: '8px',
                                }}
                            >
                                Search
                            </label>
                            <div style={{ position: 'relative' }}>
                                <input
                                    type="text"
                                    value={searchText}
                                    onChange={(e) => setSearchText(e.target.value)}
                                    placeholder="Client, ID, or location..."
                                    style={{
                                        width: '100%',
                                        padding: '10px 16px 10px 40px',
                                        backgroundColor: theme.background.panel,
                                        border: `1px solid ${theme.colors.line}`,
                                        borderRadius: theme.borderRadius.r1,
                                        color: theme.text.primary,
                                        fontSize: '14px',
                                        fontFamily: 'inherit',
                                    }}
                                />
                                <div
                                    style={{
                                        position: 'absolute',
                                        left: '12px',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        color: theme.text.muted,
                                    }}
                                >
                                    <CiSearch size={18} />
                                </div>
                            </div>
                        </div>

                        <div>
                            <label
                                style={{
                                    display: 'block',
                                    fontSize: '14px',
                                    fontWeight: 500,
                                    color: theme.text.primary,
                                    marginBottom: '8px',
                                }}
                            >
                                Service
                            </label>
                            <select
                                value={selectedService}
                                onChange={(e) => setSelectedService(e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '10px 16px',
                                    backgroundColor: theme.background.panel,
                                    border: `1px solid ${theme.colors.line}`,
                                    borderRadius: theme.borderRadius.r1,
                                    color: theme.text.primary,
                                    fontSize: '14px',
                                    fontFamily: 'inherit',
                                }}
                            >
                                <option value="">All</option>
                                {uniqueServices.map(service => (
                                    <option key={service} value={service}>{service}</option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label
                                style={{
                                    display: 'block',
                                    fontSize: '14px',
                                    fontWeight: 500,
                                    color: theme.text.primary,
                                    marginBottom: '8px',
                                }}
                            >
                                Sector
                            </label>
                            <select
                                value={selectedSector}
                                onChange={(e) => setSelectedSector(e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '10px 16px',
                                    backgroundColor: theme.background.panel,
                                    border: `1px solid ${theme.colors.line}`,
                                    borderRadius: theme.borderRadius.r1,
                                    color: theme.text.primary,
                                    fontSize: '14px',
                                    fontFamily: 'inherit',
                                }}
                            >
                                <option value="">All</option>
                                {uniqueSectors.map(sector => (
                                    <option key={sector} value={sector}>{sector}</option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label
                                style={{
                                    display: 'block',
                                    fontSize: '14px',
                                    fontWeight: 500,
                                    color: theme.text.primary,
                                    marginBottom: '8px',
                                }}
                            >
                                Location
                            </label>
                            <select
                                value={selectedLocation}
                                onChange={(e) => setSelectedLocation(e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '10px 16px',
                                    backgroundColor: theme.background.panel,
                                    border: `1px solid ${theme.colors.line}`,
                                    borderRadius: theme.borderRadius.r1,
                                    color: theme.text.primary,
                                    fontSize: '14px',
                                    fontFamily: 'inherit',
                                }}
                            >
                                <option value="">All</option>
                                {uniqueLocations.map(location => (
                                    <option key={location} value={location}>{location}</option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <Button
                                variant="secondary"
                                onClick={clearFilters}
                                style={{ padding: '10px 16px', whiteSpace: 'nowrap' }}
                            >
                                Clear
                            </Button>
                        </div>
                    </div>
                </div>

                {filteredProjects.length === 0 ? (
                    <EmptyState
                        icon={<CiSearch />}
                        title="No projects found"
                        description="Try adjusting your filters to see more results."
                        actions={[
                            { label: 'Clear Filters', path: '/projects', variant: 'secondary' },
                            { label: 'View All Projects', path: '/projects', variant: 'primary' },
                        ]}
                    />
                ) : (
                    <>
                        <div
                            style={{
                                display: 'grid',
                                gridTemplateColumns: breakpoint.xs || breakpoint.sm ? '1fr' : breakpoint.md ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
                                gap: '24px',
                                marginBottom: '32px',
                            }}
                        >
                            {paginatedProjects.map((project) => (
                                <Card key={project.id} hoverable style={{
                                    display: 'flex', flexDirection: 'column'
                                }}>
                                    {/* <PlaceholderImage
                                    label="PROJECT THUMBNAIL"
                                    height={180}
                                    style={{ marginBottom: '16px', borderRadius: theme.borderRadius.r1 }}
                                /> */}
                                    <div
                                        style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'flex-start',
                                            marginBottom: '12px',
                                            gap: '8px',
                                        }}
                                    >
                                        <div
                                            style={{
                                                fontSize: '12px',
                                                fontWeight: 600,
                                                padding: '4px 10px',
                                                borderRadius: '12px',
                                                border: '1px solid',
                                                ...getPillStyle(theme, project.primaryServiceLine),
                                            }}
                                        >
                                            {project.id}
                                        </div>
                                        <div
                                            style={{
                                                fontSize: '12px',
                                                fontWeight: 500,
                                                padding: '4px 10px',
                                                borderRadius: '12px',
                                                border: '1px solid',
                                                ...getPillStyle(theme, project.sector),
                                            }}
                                        >
                                            {project.projectType}
                                        </div>
                                    </div>
                                    <h3
                                        style={{
                                            fontSize: '18px',
                                            fontWeight: 600,
                                            color: theme.text.primary,
                                            marginBottom: '8px',
                                        }}
                                    >
                                        {project.client}
                                    </h3>
                                    <div
                                        style={{
                                            fontSize: '14px',
                                            color: theme.text.muted,
                                            marginBottom: '8px',
                                        }}
                                    >
                                        <div>Sector: {project.sector}</div>
                                        <div>Location: {project.location}</div>
                                        <div>Service: {project.primaryServiceLine}</div>
                                    </div>
                                    <p
                                        style={{
                                            fontSize: '13px',
                                            color: theme.text.muted,
                                            lineHeight: 1.5,
                                            marginBottom: '16px',
                                        }}
                                    >
                                        {project.oneSentenceOutcome}
                                    </p>
                                    <Link to={`/projects/${project.id}`} style={{ textDecoration: 'none', marginTop: 'auto' }}>
                                        <Button variant="secondary" style={{ width: '100%' }}>
                                            View case study
                                        </Button>
                                    </Link>
                                </Card>
                            ))}
                        </div>

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    gap: '8px',
                                    marginTop: '32px',
                                }}
                            >
                                <Button
                                    variant="secondary"
                                    onClick={() => {
                                        const newPage = Math.max(1, currentPage - 1);
                                        setCurrentPage(newPage);
                                    }}
                                    disabled={currentPage === 1}
                                    style={{ padding: '8px 16px' }}
                                >
                                    Previous
                                </Button>

                                <div
                                    style={{
                                        display: 'flex',
                                        gap: '4px',
                                        alignItems: 'center',
                                    }}
                                >
                                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                        let pageNum: number;
                                        if (totalPages <= 5) {
                                            pageNum = i + 1;
                                        } else if (currentPage <= 3) {
                                            pageNum = i + 1;
                                        } else if (currentPage >= totalPages - 2) {
                                            pageNum = totalPages - 4 + i;
                                        } else {
                                            pageNum = currentPage - 2 + i;
                                        }

                                        return (
                                            <button
                                                key={pageNum}
                                                onClick={() => setCurrentPage(pageNum)}
                                                style={{
                                                    padding: '8px 12px',
                                                    borderRadius: theme.borderRadius.r1,
                                                    border: `1px solid ${currentPage === pageNum ? theme.colors.primary : theme.colors.border}`,
                                                    backgroundColor: currentPage === pageNum ? theme.colors.primary : theme.colors.surface,
                                                    color: currentPage === pageNum ? theme.text.contrast : theme.text.primary,
                                                    fontSize: '14px',
                                                    fontWeight: currentPage === pageNum ? 600 : 400,
                                                    cursor: 'pointer',
                                                    transition: 'all 0.2s ease',
                                                }}
                                                onMouseEnter={(e) => {
                                                    if (currentPage !== pageNum) {
                                                        e.currentTarget.style.backgroundColor = theme.colors.surfaceAlt;
                                                    }
                                                }}
                                                onMouseLeave={(e) => {
                                                    if (currentPage !== pageNum) {
                                                        e.currentTarget.style.backgroundColor = theme.colors.surface;
                                                    }
                                                }}
                                            >
                                                {pageNum}
                                            </button>
                                        );
                                    })}
                                </div>

                                <Button
                                    variant="secondary"
                                    onClick={() => {
                                        const newPage = Math.min(totalPages, currentPage + 1);
                                        setCurrentPage(newPage);
                                    }}
                                    disabled={currentPage === totalPages}
                                    style={{ padding: '8px 16px' }}
                                >
                                    Next
                                </Button>
                            </div>
                        )}

                        <div
                            style={{
                                textAlign: 'center',
                                marginTop: '16px',
                                fontSize: '14px',
                                color: theme.text.muted,
                            }}
                        >
                            Showing {startIndex + 1}–{Math.min(endIndex, filteredProjects.length)} of {filteredProjects.length} projects
                        </div>
                    </>
                )}
            </Section>

            <Section
                style={{
                    backgroundColor: theme.background.panel,
                }}
            >
                <div
                    style={{
                        maxWidth: '800px',
                        margin: '0 auto',
                        textAlign: 'center',
                    }}
                >
                    <h2
                        style={{
                            fontSize: breakpoint.xs || breakpoint.sm ? '28px' : '34px',
                            fontWeight: 700,
                            color: theme.text.primary,
                            marginBottom: '16px',
                        }}
                    >
                        Tell us what you're planning
                    </h2>
                    <p
                        style={{
                            fontSize: '16px',
                            color: theme.text.muted,
                            marginBottom: '24px',
                        }}
                    >
                        {megaGreenData.contact.pageIntro}
                    </p>
                    <Link to="/contact" style={{ textDecoration: 'none' }}>
                        <Button variant="primary" style={{ padding: '12px 32px' }}>
                            Request a Consultation
                        </Button>
                    </Link>
                </div>
            </Section>
        </>
    );
};

